/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import {
    ArrowRight,
    Building2,
    ExternalLink,
    FilePlus2,
    ListChecks,
    RefreshCw,
    ShieldCheck,
    Zap,
} from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import {
    RECRUITMENT_API_DOCS_CONTENT,
    RECRUITMENT_API_DOCS_LANGUAGES,
    RECRUITMENT_API_DOCS_PREVIEW_MODES,
    buildRecruitmentApiAuthSnippet,
    buildRecruitmentApiCurlSnippet,
    buildRecruitmentApiJavaScriptSnippet,
    buildRecruitmentApiRequestExample,
    buildRecruitmentApiRequestTypings,
    buildRecruitmentApiResponseExample,
    buildRecruitmentApiResponseTypings,
    formatRecruitmentApiJson,
    type RecruitmentApiDocCodeLanguage,
    type RecruitmentApiDocField,
    type RecruitmentApiDocFieldLocation,
    type RecruitmentApiDocPreviewMode,
    type RecruitmentApiDocSection,
    type RecruitmentApiDocSectionId,
} from '@/client/catalogs/marketing/api/recruitmentPageContent';
import { buildContactHref } from '@/client/catalogs/marketing/navigation';
import Page from '@/client/pages/_layout/_page';
import { getRecruitersAppUrl } from '@/common/config/router';
import useHeader from '@client/pages/useHeader';
import CopyableCodeBlock from './CopyableCodeBlock';

/*----------------------------------
- TYPES
----------------------------------*/

export interface RecruitmentApiDocumentationPageProps {
    currentOrigin: string;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const LOCATION_LABELS: Record<RecruitmentApiDocFieldLocation, string> = {
    header: 'Header',
    body: 'Body',
    path: 'Path',
    response: 'Response',
};

const RECRUITMENT_API_CONTACT_HREF = buildContactHref({
    topic: 'api-access',
    source: 'recruitment-api',
});

/*----------------------------------
- HELPERS
----------------------------------*/

const getSectionIdFromHash = (hash: string): RecruitmentApiDocSectionId | null => {
    const normalized = hash.replace(/^#/, '').trim();
    if (!normalized) return null;

    const match = RECRUITMENT_API_DOCS_CONTENT.sections.find((section) => section.id === normalized);
    return match?.id || null;
};

const getSectionIcon = (sectionId: RecruitmentApiDocSectionId) => {
    switch (sectionId) {
        case 'overview':
            return ShieldCheck;
        case 'companies':
            return Building2;
        case 'create-mission':
            return FilePlus2;
        case 'list-missions':
            return ListChecks;
        case 'update-mission':
            return RefreshCw;
    }
};

const hasRequestBodyPreview = (sectionId: RecruitmentApiDocSectionId) => {
    switch (sectionId) {
        case 'companies':
        case 'create-mission':
        case 'update-mission':
            return true;
        case 'overview':
        case 'list-missions':
            return false;
    }
};

const renderFieldRows = (fields: readonly RecruitmentApiDocField[]) => {
    return fields.map((field) => (
        <div
            key={`${field.location}:${field.name}`}
            className="grid gap-3 border-t border-slate-200 px-5 py-4 first:border-t-0 md:grid-cols-[190px_140px_minmax(0,1fr)]"
        >
            <div>
                <p className="text-sm font-semibold text-slate-900">{field.name}</p>
                <p className="mt-1 text-xs text-slate-500">{LOCATION_LABELS[field.location]}</p>
            </div>
            <div className="flex flex-col gap-2">
                <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {field.type}
                </span>
                <span
                    className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                        field.required
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-cyan-100 text-cyan-700'
                    }`}
                >
                    {field.required ? 'Required' : 'Optional'}
                </span>
            </div>
            <p className="text-sm leading-6 text-slate-600">{field.description}</p>
        </div>
    ));
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function RecruitmentApiDocumentationPage({
    currentOrigin,
}: RecruitmentApiDocumentationPageProps) {
    useHeader({
        title: 'Recruitment API',
        description:
            'Recruitment API documentation for creating companies, opening missions, listing owned records, and patching job descriptions.',
    });

    const recruitersAppBaseUrl = React.useMemo(() => getRecruitersAppUrl(currentOrigin), [currentOrigin]);
    const [selectedSectionId, setSelectedSectionId] = React.useState<RecruitmentApiDocSectionId>('overview');
    const [selectedLanguage, setSelectedLanguage] = React.useState<RecruitmentApiDocCodeLanguage>('curl');
    const [selectedPreviewMode, setSelectedPreviewMode] = React.useState<RecruitmentApiDocPreviewMode>('example');

    const selectedSection = React.useMemo<RecruitmentApiDocSection>(() => {
        return (
            RECRUITMENT_API_DOCS_CONTENT.sections.find((section) => section.id === selectedSectionId)
            || RECRUITMENT_API_DOCS_CONTENT.sections[0]
        );
    }, [selectedSectionId]);

    const requestPreview = React.useMemo(() => {
        if (!hasRequestBodyPreview(selectedSection.id)) return '';

        return selectedPreviewMode === 'typings'
            ? buildRecruitmentApiRequestTypings(selectedSection.id)
            : formatRecruitmentApiJson(buildRecruitmentApiRequestExample(selectedSection.id));
    }, [selectedPreviewMode, selectedSection.id]);

    const responsePreview = React.useMemo(() => {
        return selectedPreviewMode === 'typings'
            ? buildRecruitmentApiResponseTypings(selectedSection.id)
            : formatRecruitmentApiJson(buildRecruitmentApiResponseExample(selectedSection.id));
    }, [selectedPreviewMode, selectedSection.id]);

    const activeSnippet = React.useMemo(() => {
        if (selectedSection.id === 'overview') {
            return buildRecruitmentApiAuthSnippet();
        }

        return selectedLanguage === 'curl'
            ? buildRecruitmentApiCurlSnippet(selectedSection.id, recruitersAppBaseUrl)
            : buildRecruitmentApiJavaScriptSnippet(selectedSection.id, recruitersAppBaseUrl);
    }, [recruitersAppBaseUrl, selectedLanguage, selectedSection.id]);

    const activeSnippetLanguage = React.useMemo(() => {
        if (selectedSection.id === 'overview') return 'text';

        return selectedLanguage === 'javascript' ? 'typescript' : 'bash';
    }, [selectedLanguage, selectedSection.id]);

    const previewLanguage = selectedPreviewMode === 'typings' ? 'typescript' : 'json';

    const activeSnippetCaption = React.useMemo(() => {
        if (selectedSection.id === 'overview') return 'Header contract used by the Recruitment API';
        if (selectedSection.id === 'list-missions') return 'Server-to-server read request example';

        return selectedLanguage === 'curl'
            ? 'Server-to-server write request example'
            : 'JavaScript backend request example';
    }, [selectedLanguage, selectedSection.id]);

    const activeSnippetTitle = React.useMemo(() => {
        if (selectedSection.id === 'overview') return 'Authentication headers';

        return selectedLanguage === 'curl' ? 'cURL example' : 'JavaScript fetch example';
    }, [selectedLanguage, selectedSection.id]);

    React.useEffect(() => {
        const hashSectionId = getSectionIdFromHash(window.location.hash);
        if (hashSectionId) {
            setSelectedSectionId(hashSectionId);
        }
    }, []);

    const handleSelectSection = React.useCallback((sectionId: RecruitmentApiDocSectionId) => {
        setSelectedSectionId(sectionId);
        window.history.replaceState(null, '', `#${sectionId}`);
    }, []);

    return (
        <Page withLayout={false}>
            <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(6,182,212,0.16),_transparent_30%),linear-gradient(180deg,_#ecfeff_0%,_#ffffff_36%,_#f0fdf4_100%)]" data-testid="recruitment-api-page">
                <PublicHeader currentPage="partners" ctaLabel="Get API Access" ctaHref={RECRUITMENT_API_CONTACT_HREF} />

                <section className="px-6 pb-16 pt-32 lg:px-8 lg:pb-20">
                    <div className="mx-auto max-w-7xl">
                        <div className="max-w-4xl">
                            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm">
                                <Zap size={16} />
                                {RECRUITMENT_API_DOCS_CONTENT.hero.eyebrow}
                            </div>
                            <h1 className="mt-6 max-w-4xl text-5xl font-bold tracking-tight text-slate-950 md:text-6xl">
                                {RECRUITMENT_API_DOCS_CONTENT.hero.title}
                            </h1>
                            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
                                {RECRUITMENT_API_DOCS_CONTENT.hero.description}
                            </p>
                            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                {RECRUITMENT_API_DOCS_CONTENT.hero.highlights.map((highlight) => (
                                    <div key={highlight.label} className="rounded-2xl border border-white/80 bg-white/90 px-4 py-4 shadow-sm">
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{highlight.label}</p>
                                        <p className="mt-2 text-sm font-semibold text-slate-900">{highlight.value}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                                <a
                                    href={RECRUITMENT_API_CONTACT_HREF}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-7 py-4 text-base font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:from-emerald-600 hover:to-cyan-600"
                                >
                                    Get API Access
                                    <ArrowRight size={18} />
                                </a>
                                <a
                                    href={recruitersAppBaseUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                                >
                                    Open recruiters app
                                    <ExternalLink size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="px-6 pb-20 lg:px-8 lg:pb-24">
                    <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start">
                        <aside className="lg:sticky lg:top-28 lg:self-start">
                            <div className="rounded-3xl border border-emerald-200/80 bg-white/90 p-5 shadow-lg shadow-emerald-100/60">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Sections</p>
                                <div className="mt-4 space-y-2">
                                    {RECRUITMENT_API_DOCS_CONTENT.sections.map((section) => {
                                        const Icon = getSectionIcon(section.id);
                                        const isActive = section.id === selectedSectionId;

                                        return (
                                            <button
                                                key={section.id}
                                                type="button"
                                                onClick={() => handleSelectSection(section.id)}
                                                data-testid={`recruitment-api-nav-${section.id}`}
                                                className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                                                    isActive
                                                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm'
                                                        : 'border-transparent bg-slate-50 text-slate-600 hover:border-slate-200 hover:bg-white'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`rounded-xl p-2 ${isActive ? 'bg-emerald-100' : 'bg-white'}`}>
                                                        <Icon size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold">{section.label}</p>
                                                        <p className="mt-1 text-xs leading-5 text-slate-500">{section.eyebrow}</p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                        </aside>

                        <div className="space-y-8">
                            <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/95 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.65)]">
                                <div className="border-b border-slate-200 px-6 py-6 sm:px-8">
                                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600">
                                        {selectedSection.eyebrow}
                                    </p>
                                    <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div>
                                            <h2 className="text-3xl font-bold tracking-tight text-slate-950" data-testid="recruitment-api-panel-title">
                                                {selectedSection.title}
                                            </h2>
                                            <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                                                {selectedSection.summary}
                                            </p>
                                        </div>
                                        {selectedSection.method && selectedSection.path ? (
                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-right">
                                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                                    Endpoint
                                                </p>
                                                <p className="mt-2 text-sm font-semibold text-emerald-700">{selectedSection.method}</p>
                                                <p className="mt-1 font-mono text-sm text-slate-800">{selectedSection.path}</p>
                                            </div>
                                        ) : null}
                                    </div>
                                    <p className="mt-5 max-w-3xl text-sm leading-6 text-slate-600">{selectedSection.description}</p>
                                    <ul className="mt-6 grid gap-3 sm:grid-cols-3">
                                        {selectedSection.bullets.map((bullet) => (
                                            <li key={bullet} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-700">
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {selectedSection.requestFields.length ? (
                                    <div className="border-b border-slate-200">
                                        <div className="px-6 py-5 sm:px-8">
                                            <h3 className="text-lg font-semibold text-slate-950">Request contract</h3>
                                            <p className="mt-1 text-sm text-slate-500">Headers, path parameters, and body fields used by this step.</p>
                                        </div>
                                        {renderFieldRows(selectedSection.requestFields)}
                                    </div>
                                ) : null}

                                {selectedSection.responseFields.length ? (
                                    <div>
                                        <div className="px-6 py-5 sm:px-8">
                                            <h3 className="text-lg font-semibold text-slate-950">Response contract</h3>
                                            <p className="mt-1 text-sm text-slate-500">The documented response fields returned by CrossPath.</p>
                                        </div>
                                        {renderFieldRows(selectedSection.responseFields)}
                                    </div>
                                ) : null}

                                {selectedSection.id === 'overview' ? (
                                    <div className="border-t border-slate-200 px-6 py-6 sm:px-8">
                                        <h3 className="text-lg font-semibold text-slate-950">Error responses</h3>
                                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                                            {RECRUITMENT_API_DOCS_CONTENT.errorExamples.map((errorExample) => (
                                                <div key={errorExample.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                                    <p className="text-sm font-semibold text-slate-900">{errorExample.title}</p>
                                                    <p className="mt-2 font-mono text-xs leading-6 text-slate-600">{errorExample.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-6">
                                <div className="rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-lg shadow-slate-100/70">
                                    {selectedSection.id !== 'overview' ? (
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Code examples</p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {RECRUITMENT_API_DOCS_LANGUAGES.map((language) => (
                                                    <button
                                                        key={language.id}
                                                        type="button"
                                                        onClick={() => setSelectedLanguage(language.id)}
                                                        data-testid={`recruitment-api-language-${language.id}`}
                                                        className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                                            selectedLanguage === language.id
                                                                ? 'bg-slate-950 text-white'
                                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                        }`}
                                                    >
                                                        {language.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className={selectedSection.id !== 'overview' ? 'mt-5 border-t border-slate-200 pt-5' : ''}>
                                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Payload view</p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {RECRUITMENT_API_DOCS_PREVIEW_MODES.map((mode) => (
                                                <button
                                                    key={mode.id}
                                                    type="button"
                                                    onClick={() => setSelectedPreviewMode(mode.id)}
                                                    data-testid={`recruitment-api-preview-${mode.id}`}
                                                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                                        selectedPreviewMode === mode.id
                                                            ? 'bg-emerald-500 text-white'
                                                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                                    }`}
                                                >
                                                    {mode.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <CopyableCodeBlock
                                    title={activeSnippetTitle}
                                    caption={activeSnippetCaption}
                                    code={activeSnippet}
                                    testId="recruitment-api-snippet"
                                    language={activeSnippetLanguage}
                                />

                                {requestPreview ? (
                                    <CopyableCodeBlock
                                        title={selectedPreviewMode === 'typings' ? 'TypeScript request typings' : 'JSON request example'}
                                        caption={selectedPreviewMode === 'typings'
                                            ? 'Typed request shape for the selected Recruitment API endpoint'
                                            : 'The request body sent to the selected Recruitment API endpoint'}
                                        code={requestPreview}
                                        testId="recruitment-api-request"
                                        language={previewLanguage}
                                    />
                                ) : null}

                                <CopyableCodeBlock
                                    title={selectedPreviewMode === 'typings' ? 'TypeScript response typings' : 'JSON response preview'}
                                    caption={selectedPreviewMode === 'typings'
                                        ? 'Typed response shape returned by CrossPath'
                                        : 'Representative response payload'}
                                    code={responsePreview}
                                    testId="recruitment-api-response"
                                    language={previewLanguage}
                                />
                            </div>

                            <div className="rounded-[28px] border border-emerald-200 bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-8 text-white shadow-2xl shadow-emerald-200/60 sm:px-8">
                                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-50">
                                            Need access?
                                        </p>
                                        <h3 className="mt-3 text-2xl font-bold">Request the Recruitment API key and field mapping.</h3>
                                        <p className="mt-3 max-w-2xl text-sm leading-6 text-emerald-50">
                                            CrossPath will issue the technical account, API key, and accepted reference values for sectors, locations, languages, positions, and company sizes.
                                        </p>
                                    </div>
                                    <a
                                        href={RECRUITMENT_API_CONTACT_HREF}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-emerald-700 transition hover:-translate-y-0.5 hover:text-emerald-800"
                                    >
                                        Get API Access
                                        <ArrowRight size={18} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <PublicFooter tagline="Document one recruitment contract, then keep companies and job descriptions synchronized." />
            </div>
        </Page>
    );
}
